// src/api/axiosClient.js
import axios from "axios";
// Live bindings: populated by initAuth() during bootstrap, read at call time.
import { msalInstance, loginRequest } from "../authConfig";
import { getSessionId, recordRequest, requestPath } from "../feedback/sessionBuffer";
import { captureRuntimeHeaders } from "../utils/runtimeInfo";
import { isSessionRefreshDetail, showSessionRefreshNotice } from "./sessionRefresh";

// Same-origin: /api is proxied to Django by the CRA dev server locally and by
// the Azure Static Web Apps linked backend when hosted.
const axiosClient = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

async function tryGetToken() {
  const account = msalInstance?.getActiveAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return result.accessToken;
  } catch (e) {
    console.warn("Silent token acquisition failed:", e);
    return null;
  }
}

axiosClient.interceptors.request.use(async (config) => {
  const token = await tryGetToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-Correlation-ID"] = getSessionId();
  config.__startedAt = Date.now();
  return config;
});

// Response interceptor to handle re-authentication required
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      isSessionRefreshDetail(error.response?.data?.detail)
    ) {
      showSessionRefreshNotice(error.response.data.detail);
    }
    return Promise.reject(error);
  }
);

function recordCompletedRequest(config, status) {
  if (!config) return;
  const startedAt = config.__startedAt;
  recordRequest({
    method: (config.method || "get").toUpperCase(),
    path: requestPath(config),
    status,
    durationMs: startedAt ? Date.now() - startedAt : 0,
  });
}

axiosClient.interceptors.response.use(
  (response) => {
    recordCompletedRequest(response.config, response.status);
    captureRuntimeHeaders(response?.headers);
    return response;
  },
  (error) => {
    recordCompletedRequest(error.config, error.response?.status || 0);
    captureRuntimeHeaders(error?.response?.headers);
    return Promise.reject(error);
  }
);

export default axiosClient;
